import {
  EmbeddedDataModule,
  EmbeddedDataService,
  NORMALIZE_URL_SERVICE_CONFIG_TOKEN,
  NormalizeUrlModule,
} from '@allianz/ngx-aem-integration';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NxDatepickerIntl } from '@aposin/ng-aquila/datefield';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxPrintModule } from 'ngx-print';
import { QbeComponentsModule } from 'qbe-components';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AffiliateLinksInfoComponent } from './component/affiliate-links-info/affiliate-links-info.component';
import { AgreementsComponent } from './component/agreements/agreements.component';
import { AppHeaderComponent } from './component/app-header/app-header.component';
import { CoverageInformationComponent } from './component/coverage-information/coverage-information.component';
import { DiscountCodeComponent } from './component/discount-code/discount-code.component';
import { DynamicInputArrayComponent } from './component/dynamic-input-array/dynamic-input-array.component';
import { DynamicInputComponent } from './component/dynamic-input/dynamic-input.component';
import { ErrorComponent } from './component/error/error.component';
import { FabComponent } from './component/fab/fab.component';
import { FormDebugComponent } from './component/form-debug/form-debug.component';
import { FormElementComponent } from './component/form-element/form-element.component';
import { IllustrationAbsoluteComponent } from './component/illustration-absolute/illustration-absolute.component';
import { LoadPolicyDataComponent } from './component/load-policy-data/load-policy-data.component';
import { LoadPolicyMessagesComponent } from './component/load-policy-messages/load-policy-messages.component';
import { LoaderComponent } from './component/loader/loader.component';
import { ModalCloseComponent } from './component/modal-close/modal-close.component';
import { ChangePremiumComponent } from './component/modals/change-premium/change-premium.component';
import { CheckDocumentsComponent } from './component/modals/check-documents/check-documents.component';
import { CompareOffersComponent } from './component/modals/compare-offers/compare-offers.component';
import { ConclusionRemoteComponent } from './component/modals/conclusion-remote/conclusion-remote.component';
import { ContactAgentComponent } from './component/modals/contact-agent/contact-agent.component';
import { CustomizeAlcoholComponent } from './component/modals/customize-alcohol/customize-alcohol.component';
import { GdprComponent } from './component/modals/gdpr/gdpr.component';
import { HighRiskComponent } from './component/modals/high-risk/high-risk.component';
import { InsuredImportComponent } from './component/modals/insured-import/insured-import.component';
import { LeisureSportsComponent } from './component/modals/leisure-sports/leisure-sports.component';
import { OfferLeafletComponent } from './component/modals/offer-leaflet/offer-leaflet.component';
import { ResignationCancelComponent } from './component/modals/resignation-cancel/resignation-cancel.component';
import { ResignationInterruptComponent } from './component/modals/resignation-interrup/resignation-interrupt.component';
import { NavigationErrorComponent } from './component/navigation-error/navigation-error.component';
import { NavigationRowComponent } from './component/navigation-row/navigation-row.component';
import { NavigationComponent } from './component/navigation/navigation.component';
import { OfferContentComponent } from './component/offer-content/offer-content.component';
import { OfferTableComponent } from './component/offer-table/offer-table.component';
import { PageHeaderComponent } from './component/page-header/page-header.component';
import { PriceTagComponent } from './component/price-tag/price-tag.component';
import { ProgressStepperComponent } from './component/progress-stepper/progress-stepper.component';
import { RadioButtonsComponent } from './component/radio-buttons/radio-buttons.component';
import { OutOfScreenDirective } from './directive/out-of-screen.directive';
import { environment } from './environments/environment';
import { ResignationComponent } from './form/resignation/resignation.component';
import { TravelInPlComponent } from './form/travel-in-pl/travel-in-pl.component';
import { TripAbroadComponent } from './form/trip-abroad/trip-abroad.component';
import { initializeTranslations } from './initializer/translations.initializer';
import { AuthInterceptor } from './interceptor/auth.interceptor';
import { DateFixInterceptor } from './interceptor/date-fix.interceptor';
import { LangInterceptor } from './interceptor/lang.interceptor';
import { RequestCounterInterceptor } from './interceptor/request-counter.interceptor';
import { CustomTranslateLoader } from './loader/customTranslate.loader';
import { NdbxModule } from './ndbx.module';
import { AgentSuccessComponent } from './page/agent-success/agent-success.component';
import { AuthenticationComponent } from './page/authentication/authentication.component';
import { ConclusionAgentComponent } from './page/conclusion-agent/conclusion-agent.component';
import { PageConclusionRemoteComponent } from './page/conclusion-remote/conclusion-remote.component';
import { ConclusionRequestComponent } from './page/conclusion-request/conclusion-request.component';
import { ConclusionComponent } from './page/conclusion/conclusion.component';
import { DevComponent } from './page/dev/dev.component';
import { PageErrorComponent } from './page/error/error.component';
import { PageLoaderComponent } from './page/loader/loader.component';
import { PageOfferComponent } from './page/offer/offer.component';
import { PaymentStatusComponent } from './page/payment-status/payment-status.component';
import { PaymentComponent } from './page/payment/payment.component';
import { RemoteIdConsentsComponent } from './page/remote-id-consents/remote-id-consents.component';
import { RemoteIdOtpComponent } from './page/remote-id-otp/remote-id-otp.component';
import { RemoteIdComponent } from './page/remote-id/remote-id.component';
import { Step1Component } from './page/step1/step1.component';
import { Step2Component } from './page/step2/step2.component';
import { Step4Component } from './page/step4/step4.component';
import { Step5Component } from './page/step5/step5.component';
import { Step6Component } from './page/step6/step6.component';
import { SuccessComponent } from './page/success/success.component';
import { AztCurrency } from './pipe/azt-currency.pipe';
import { AztDatePipe } from './pipe/azt-date.pipe';
import { IsNotEmptyPipe } from './pipe/is-not-empty.pipe';
import { MarkdownPipe } from './pipe/markdown.pipe';
import { NxDateFieldIntlProvider } from './providers/nx-date-field-intl.provider';
import appStore, { hydrationMetaReducer } from './store';
import { AgreementsEffects } from './store/agreements/agreements.effects';
import { AttachmentsEffects } from './store/attachments/attachments.effects';
import { ConfigEffects } from './store/config/config.effects';
import { InsuredImportEffects } from './store/insured-import/insured-import.effects';
import { PaymentsEffects } from './store/payments/payments.effects';
import { ProcessEffects } from './store/process/process.effects';
import { RemoteEffects } from './store/remote/remote.effects';
import { SaveContactEffects } from './store/save-contact/save-contact.effects';
import { TrackingModule } from './tracking/tracking.module';
import { CustomizeForTheActiveComponent } from './component/modals/customioze-for-the-active/customize-for-the-active.component';
import { ResignationAddonNamePipe } from './pipe/resignation-addon.pipe';
import { MobileMenuComponent } from './component/app-header/mobile-menu/mobile-menu.component';

const components = [
  AppComponent,
  PageErrorComponent,
  DevComponent,
  ChangePremiumComponent,
  CompareOffersComponent,
  CustomizeAlcoholComponent,
  CustomizeForTheActiveComponent,
  CheckDocumentsComponent,
  ProgressStepperComponent,
  PriceTagComponent,
  Step1Component,
  Step2Component,
  Step4Component,
  Step5Component,
  Step6Component,
  SuccessComponent,
  NavigationComponent,
  LoaderComponent,
  TravelInPlComponent,
  DynamicInputComponent,
  FormElementComponent,
  DynamicInputArrayComponent,
  TripAbroadComponent,
  ResignationComponent,
  ErrorComponent,
  PaymentComponent,
  PaymentStatusComponent,
  CoverageInformationComponent,
  ConclusionComponent,
  PageHeaderComponent,
  RadioButtonsComponent,
  ConclusionAgentComponent,
  ConclusionRemoteComponent,
  NavigationErrorComponent,
  AuthenticationComponent,
  ConclusionRequestComponent,
  AgentSuccessComponent,
  RemoteIdComponent,
  IllustrationAbsoluteComponent,
  RemoteIdOtpComponent,
  RemoteIdConsentsComponent,
  FormDebugComponent,
  PageConclusionRemoteComponent,
  AgreementsComponent,
  PageLoaderComponent,
  PageOfferComponent,
  OfferContentComponent,
  DiscountCodeComponent,
  OfferTableComponent,
  LeisureSportsComponent,
  ModalCloseComponent,
  NavigationRowComponent,
  AffiliateLinksInfoComponent,
  AppHeaderComponent,
  MobileMenuComponent,
];

const directives = [OutOfScreenDirective];

const modals = [ContactAgentComponent, HighRiskComponent, GdprComponent];

const pipes = [AztDatePipe, AztCurrency, MarkdownPipe, IsNotEmptyPipe, ResignationAddonNamePipe];

@NgModule({
  declarations: [
    components,
    directives,
    modals,
    pipes,
    ResignationCancelComponent,
    ResignationInterruptComponent,
    FabComponent,
    InsuredImportComponent,
    LoadPolicyDataComponent,
    LoadPolicyMessagesComponent,
    OfferLeafletComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    FormsModule,
    NdbxModule,
    QbeComponentsModule,
    AppRoutingModule,
    StoreModule.forRoot(appStore, { metaReducers: [hydrationMetaReducer] }),
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !environment.debug }),
    EffectsModule.forRoot([
      AttachmentsEffects,
      ConfigEffects,
      AgreementsEffects,
      ProcessEffects,
      PaymentsEffects,
      SaveContactEffects,
      RemoteEffects,
      InsuredImportEffects,
    ]),
    HttpClientModule,
    EmbeddedDataModule.forRoot({
      rootElementTagName: 'app-root',
      AssetsBasePath: '/assets',
    }),
    NormalizeUrlModule.forRoot({
      provide: NORMALIZE_URL_SERVICE_CONFIG_TOKEN,
      useValue: {
        deployUrl: environment.deployUrl,
      },
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateLoader,
        deps: [HttpClient],
      },
      isolate: true,
    }),
    TrackingModule.forRoot({
      debug: environment.debug,
    }),
    NgxPrintModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTranslations,
      deps: [TranslateService, Injector, EmbeddedDataService],
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: LangInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RequestCounterInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: DateFixInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: NxDatepickerIntl, useClass: NxDateFieldIntlProvider },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
